import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import {
  Check,
  Users,
  BarChart3,
  FileText,
  ClipboardList,
  Zap,
  Shield,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
  ArrowRight,
  TrendingUp,
  Package,
  Receipt,
  MessageSquare,
  Bell,
} from 'lucide-react';
import {
  motion,
  useScroll,
  useTransform,
  useSpring,
  useInView,
  useMotionValue,
  useMotionValueEvent,
  AnimatePresence,
  useReducedMotion,
} from 'framer-motion';
import Lenis from 'lenis';
import { Link } from 'react-router-dom';

import AutolinkLogo from './assets/logo.png';
import AutolinkIcon from './assets/AutolinkIcon.png';
import IMG_Dashboard from './assets/Dashboard.png';
import IMG_Inventory from './assets/Inventory.png';
import IMG_Leads from './assets/Leads.png';
import IMG_Tasks from './assets/Tasks.png';
import IMG_BOSTemplates from './assets/3BOSTemplates.png';
import IMG_TeamHub from './assets/TeamHub.png';
import IMG_ExpenseTracker from './assets/ExpenseTracker.png';

/* ------------------------------------------------------------------ *
 *  Brand tokens
 * ------------------------------------------------------------------ */
const NAVY = '#071a49';
const NAVY_DEEP = '#050f33';
const NAVY_INK = '#03081f';
const GOLD = '#f5c018';
const GOLD_DEEP = '#9a7410'; // accessible gold for small text on white
const EASE = [0.16, 1, 0.3, 1];

// light theme (sections between hero and footer)
const SURFACE = '#ffffff';
const SURFACE_ALT = '#f4f7fc';
const INK = '#071a49'; // headings on light
const BODY = '#475569'; // slate-600
const MUTED = '#64748b'; // slate-500
const LINE = '#e6eaf2';

const APP_URL = process.env.REACT_APP_APP_URL || 'https://app.autolinkdealer.com';

/* ------------------------------------------------------------------ *
 *  Data
 * ------------------------------------------------------------------ */
const NAV_LINKS = [
  { label: 'Features', href: '#features' },
  { label: 'Pricing', href: '#pricing' },
  { label: 'Reviews', href: '#testimonials' },
  { label: 'FAQ', href: '#faq' },
];

/** Hero carousel — all screens. */
const GALLERY = [
  { src: IMG_Dashboard, label: 'Dashboard' },
  { src: IMG_Inventory, label: 'Inventory' },
  { src: IMG_Leads, label: 'Leads Pipeline' },
  { src: IMG_Tasks, label: 'Task Board' },
  { src: IMG_BOSTemplates, label: 'Bill of Sale' },
  { src: IMG_TeamHub, label: 'Team Hub' },
  { src: IMG_ExpenseTracker, label: 'Expenses' },
];

/** Product tour — large annotated screens, changed with arrows. */
const STORY = [
  {
    icon: Package,
    tag: 'Inventory',
    kicker: '01',
    title: 'Every vehicle, perfectly organized',
    body: 'VIN, mileage, stock number, condition, price and status — search and filter your whole lot in seconds.',
    points: ['Available, Sold & Reserved tracking', 'One-click Sell to start a deal', 'CSV bulk import'],
    img: IMG_Inventory,
    notes: [
      { x: 70, y: 16, label: 'Live status badges' },
      { x: 22, y: 64, label: 'One-click Sell' },
    ],
  },
  {
    icon: TrendingUp,
    tag: 'Analytics',
    kicker: '02',
    title: 'Know your numbers in real time',
    body: 'Revenue, gross profit, inventory count and lead activity — surfaced the moment you log in.',
    points: ['Revenue & profit charts by month', 'Lead source breakdown', 'Smart alerts for stale leads'],
    img: IMG_Dashboard,
    notes: [
      { x: 24, y: 30, label: 'Revenue & profit' },
      { x: 80, y: 56, label: 'Lead sources' },
    ],
  },
  {
    icon: Users,
    tag: 'Pipeline',
    kicker: '03',
    title: 'Never lose a hot lead again',
    body: 'Capture prospects from any source and move them Contacted → Qualified → Negotiating → Won.',
    points: ['Assign work to the right rep', 'Budgets & vehicle interest per lead', 'Status at a glance'],
    img: IMG_Leads,
    notes: [
      { x: 82, y: 24, label: 'Pipeline status' },
      { x: 20, y: 52, label: 'Budget & interest' },
    ],
  },
  {
    icon: ClipboardList,
    tag: 'Tasks',
    kicker: '04',
    title: 'Keep the whole team on track',
    body: 'A Kanban board for the lot — TODO, In Progress and Done with priorities, due dates and tags.',
    points: ['Drag tasks across columns', 'Priorities & due dates', 'Assign to any team member'],
    img: IMG_Tasks,
    notes: [
      { x: 28, y: 40, label: 'Assignees' },
      { x: 78, y: 30, label: 'Priority labels' },
    ],
  },
  {
    icon: FileText,
    tag: 'Bill of Sale',
    kicker: '05',
    title: 'Professional documents in seconds',
    body: 'Three branded bill-of-sale templates. Drop in your logo, tax rate and doc fee, then print instantly.',
    points: ['Three polished templates', 'Branded with your identity', 'Print-ready in one click'],
    img: IMG_BOSTemplates,
    notes: [{ x: 50, y: 24, label: '3 branded templates' }],
  },
];

const MORE = [
  { icon: BarChart3, title: 'Sales & Revenue', body: 'Record sales, auto-calculate profit, and apply taxes and fees.' },
  { icon: MessageSquare, title: 'Team Hub', body: 'Announcements, shared wins, goals and milestones in one feed.' },
  { icon: Receipt, title: 'Expense Tracking', body: 'Log and approve expenses and mileage reimbursements.' },
  { icon: Users, title: 'Customer Records', body: 'Every buyer, document and deal kept in a single profile.' },
  { icon: Zap, title: 'Smart Alerts', body: 'Automatic nudges for stale leads and overdue tasks.' },
  { icon: Shield, title: 'Secure & Backed Up', body: 'Encrypted in transit and at rest, with daily backups.' },
];

const PLANS = [
  {
    name: 'Starter',
    price: 79,
    description: 'For small dealerships just getting started.',
    features: ['10GB of storage', 'Lead & customer management', 'Advanced sales analytics', 'Automated bill of sale', 'Up to 2 team members', 'Task management with Kanban'],
    cta: 'Start Free Trial',
    highlight: false,
  },
  {
    name: 'Professional',
    price: 149,
    description: 'The complete solution for growing dealerships.',
    features: ['Unlimited vehicles', 'Full lead pipeline & CRM', 'Advanced sales analytics', 'Custom branded bills of sale', 'Up to 10 team members', 'Task management with Kanban', 'Priority support', 'Dealership profile page'],
    cta: 'Coming Soon',
    highlight: true,
    badge: 'Coming Soon',
    comingSoon: true,
  },
];

const FAQS = [
  { q: 'Is there a free trial?', a: 'Yes. Every plan comes with a 30-days free trial, no credit card required. You get full access to all features during the trial.' },
  { q: 'Can I import my existing inventory?', a: 'Absolutely. Bulk-import vehicles via CSV or add them manually. Our onboarding team can help with data migration.' },
  { q: 'How many users can I add?', a: 'Starter supports 2 users and Professional supports up to 10. You can always upgrade as your team grows.' },
  { q: 'Is my data secure?', a: 'Yes. All data is encrypted at rest and in transit on enterprise-grade cloud infrastructure with daily backups.' },
  { q: 'Can I cancel anytime?', a: 'Yes, no long-term contracts. Cancel anytime and your data stays accessible until your billing period ends.' },
];

/* ------------------------------------------------------------------ *
 *  Hooks
 * ------------------------------------------------------------------ */
function useIsMobile(breakpoint = 768) {
  const [mobile, setMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return mobile;
}

function useLenisScroll(enabled) {
  useEffect(() => {
    if (!enabled) return undefined;
    const lenis = new Lenis({
      duration: 1.1,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    const onClick = (e) => {
      const a = e.target.closest('a[href^="#"]');
      if (!a) return;
      const id = a.getAttribute('href');
      if (id.length < 2) return;
      const el = document.querySelector(id);
      if (el) {
        e.preventDefault();
        lenis.scrollTo(el, { offset: -72 });
      }
    };
    document.addEventListener('click', onClick);

    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('click', onClick);
      lenis.destroy();
    };
  }, [enabled]);
}

/* ------------------------------------------------------------------ *
 *  Interaction primitives
 * ------------------------------------------------------------------ */
function CursorGlow() {
  const reduced = useReducedMotion();
  const x = useMotionValue(-600);
  const y = useMotionValue(-600);
  const sx = useSpring(x, { stiffness: 120, damping: 26, mass: 0.4 });
  const sy = useSpring(y, { stiffness: 120, damping: 26, mass: 0.4 });

  useEffect(() => {
    const move = (e) => {
      x.set(e.clientX - 300);
      y.set(e.clientY - 300);
    };
    window.addEventListener('mousemove', move);
    return () => window.removeEventListener('mousemove', move);
  }, [x, y]);

  if (reduced) return null;
  return (
    <motion.div
      aria-hidden
      className="fixed top-0 left-0 z-[2] w-[600px] h-[600px] rounded-full pointer-events-none hidden md:block"
      style={{ x: sx, y: sy, background: 'radial-gradient(circle, rgba(245,192,24,0.07) 0%, rgba(245,192,24,0) 62%)' }}
    />
  );
}

function Magnetic({ children, strength = 0.3, className = '' }) {
  const reduced = useReducedMotion();
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 220, damping: 16, mass: 0.4 });
  const sy = useSpring(y, { stiffness: 220, damping: 16, mass: 0.4 });

  const onMove = (e) => {
    if (reduced || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    x.set((e.clientX - (r.left + r.width / 2)) * strength);
    y.set((e.clientY - (r.top + r.height / 2)) * strength);
  };
  const onLeave = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <motion.span ref={ref} onMouseMove={onMove} onMouseLeave={onLeave} style={{ x: sx, y: sy, display: 'inline-block' }} className={className}>
      {children}
    </motion.span>
  );
}

/* ------------------------------------------------------------------ *
 *  Shared primitives
 * ------------------------------------------------------------------ */
function Reveal({ children, delay = 0, y = 24, className = '' }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.25 });
  const reduced = useReducedMotion();
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={reduced ? false : { opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  );
}

function Annotation({ n, delay }) {
  const labelLeft = n.x > 55;
  return (
    <motion.div
      className="absolute z-10"
      style={{ left: `${n.x}%`, top: `${n.y}%` }}
      initial={{ opacity: 0, scale: 0.5 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0 }}
      transition={{ delay, duration: 0.4, ease: EASE }}
    >
      <span className="relative flex h-3.5 w-3.5">
        <span className="absolute inline-flex h-full w-full rounded-full animate-ping" style={{ background: 'rgba(245,192,24,0.55)' }} />
        <span className="relative inline-flex h-3.5 w-3.5 rounded-full" style={{ background: GOLD, boxShadow: '0 0 0 4px rgba(245,192,24,0.2)' }} />
      </span>
      <span
        className="absolute top-1/2 -translate-y-1/2 whitespace-nowrap text-xs font-semibold px-2.5 py-1 rounded-md"
        style={{
          background: 'rgba(5,15,51,0.92)',
          color: '#fff',
          border: '1px solid rgba(245,192,24,0.35)',
          [labelLeft ? 'right' : 'left']: '22px',
        }}
      >
        {n.label}
      </span>
    </motion.div>
  );
}

/** Browser/app chrome frame (light) with depth + optional annotations. */
function AppFrame({ src, label, onClick, glow = true, notes, className = '' }) {
  return (
    <div className={`relative ${className}`}>
      {glow && (
        <div aria-hidden className="absolute -inset-8 rounded-[36px] blur-3xl opacity-70" style={{ background: 'radial-gradient(55% 55% at 50% 40%, rgba(245,192,24,0.22), rgba(245,192,24,0) 70%)' }} />
      )}
      <div className="relative rounded-2xl overflow-hidden border" style={{ background: '#ffffff', borderColor: LINE, boxShadow: '0 40px 80px -32px rgba(7,26,73,0.35)' }}>
        <div className="flex items-center gap-2 px-4 py-2.5 border-b" style={{ background: '#f1f5fb', borderColor: LINE }}>
          <span className="w-2.5 h-2.5 rounded-full bg-red-400/80" />
          <span className="w-2.5 h-2.5 rounded-full bg-yellow-400/80" />
          <span className="w-2.5 h-2.5 rounded-full bg-green-400/80" />
          {label && <span className="ml-2 text-xs tnum" style={{ color: MUTED }}>app.autolink — {label}</span>}
        </div>
        <div className="relative">
          <img src={src} alt={label || 'AutoLink screenshot'} className={`w-full block ${onClick ? 'cursor-zoom-in' : ''}`} onClick={onClick} draggable={false} loading="lazy" />
          {notes && notes.map((n, i) => <Annotation key={n.label} n={n} delay={0.35 + i * 0.18} />)}
        </div>
      </div>
    </div>
  );
}

function Lightbox({ src, alt, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      onClick={onClose}
      className="fixed inset-0 z-[1000] flex items-center justify-center p-6"
      style={{ backgroundColor: 'rgba(3,8,31,0.94)', cursor: 'zoom-out' }}
    >
      <motion.img
        src={src}
        alt={alt}
        initial={{ scale: 0.92, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.94, opacity: 0 }}
        transition={{ duration: 0.28, ease: EASE }}
        onClick={(e) => e.stopPropagation()}
        className="max-w-[95vw] max-h-[90vh] w-auto rounded-xl"
        style={{ boxShadow: '0 40px 120px rgba(0,0,0,0.7)', cursor: 'default' }}
      />
      <button onClick={onClose} aria-label="Close" className="absolute top-5 right-5 w-11 h-11 rounded-full flex items-center justify-center text-white border border-white/20 bg-white/10 hover:bg-white/20 transition-colors">
        <X size={20} />
      </button>
    </motion.div>
  );
}

/* ------------------------------------------------------------------ *
 *  Scroll progress bar
 * ------------------------------------------------------------------ */
function ScrollProgress() {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 140, damping: 30, mass: 0.3 });
  return <motion.div className="fixed top-0 left-0 right-0 z-[60] h-[3px] origin-left" style={{ scaleX, background: GOLD }} />;
}

/* ------------------------------------------------------------------ *
 *  Navbar
 * ------------------------------------------------------------------ */
function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { scrollY } = useScroll();
  useMotionValueEvent(scrollY, 'change', (v) => setScrolled(v > 40));

  return (
    <motion.nav
      initial={{ y: -72, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: EASE }}
      className="fixed top-0 inset-x-0 z-50 transition-colors duration-300"
      style={{
        background: scrolled ? 'rgba(5,15,51,0.82)' : 'transparent',
        backdropFilter: scrolled ? 'blur(14px)' : 'none',
        borderBottom: scrolled ? '1px solid rgba(255,255,255,0.08)' : '1px solid transparent',
      }}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex items-center justify-between h-[72px]">
          <a href="#top" className="flex items-center gap-2.5 cursor-pointer">
            <img src={AutolinkLogo} alt="AutoLink" className="h-7 w-auto" style={{ filter: 'brightness(0) invert(1)' }} />
          </a>
          <div className="hidden md:flex items-center gap-9">
            {NAV_LINKS.map((l) => (
              <a key={l.label} href={l.href} className="text-sm font-medium text-slate-300 hover:text-white transition-colors">{l.label}</a>
            ))}
          </div>
          <div className="hidden md:flex items-center gap-2">
            <a href={APP_URL} className="text-sm font-medium text-slate-200 hover:text-white px-4 py-2 rounded-lg transition-colors">Sign in</a>
            <Magnetic strength={0.4}>
              <a href={`${APP_URL}/register`} className="group text-sm font-semibold text-[#071a49] px-4 py-2 rounded-lg flex items-center gap-1.5 transition-transform active:scale-[0.97]" style={{ background: GOLD }}>
                Start free trial
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
              </a>
            </Magnetic>
          </div>
          <button className="md:hidden p-2 text-white" onClick={() => setOpen((o) => !o)} aria-label="Menu">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: EASE }}
            className="md:hidden overflow-hidden border-t border-white/10"
            style={{ background: 'rgba(5,15,51,0.96)' }}
          >
            <div className="px-5 py-4 space-y-1">
              {NAV_LINKS.map((l) => (
                <a key={l.label} href={l.href} onClick={() => setOpen(false)} className="block py-2.5 text-slate-200 font-medium">{l.label}</a>
              ))}
              <a href={`${APP_URL}/register`} className="block w-full text-center mt-2 py-3 rounded-lg font-semibold text-[#071a49]" style={{ background: GOLD }}>Start free trial</a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}

/* ------------------------------------------------------------------ *
 *  Hero — "showroom" with road floor + carousel (arrow controlled)
 * ------------------------------------------------------------------ */
function FloatChip({ children, className = '', style, delay = 0, float = 8 }) {
  const reduced = useReducedMotion();
  return (
    <motion.div
      className={`absolute z-20 rounded-xl px-3.5 py-2.5 ${className}`}
      style={{
        background: 'rgba(5,15,51,0.86)',
        border: '1px solid rgba(255,255,255,0.12)',
        boxShadow: '0 16px 40px -16px rgba(0,0,0,0.7)',
        backdropFilter: 'blur(8px)',
        ...style,
      }}
      initial={{ opacity: 0, scale: 0.8, y: 12 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ delay, duration: 0.6, ease: EASE }}
    >
      <motion.div animate={reduced ? {} : { y: [0, -float, 0] }} transition={{ duration: 4 + delay, repeat: Infinity, ease: 'easeInOut' }}>
        {children}
      </motion.div>
    </motion.div>
  );
}

function HeroCarousel({ onImageClick }) {
  const reduced = useReducedMotion();
  const [idx, setIdx] = useState(0);
  const timer = useRef(null);

  const restart = useCallback(() => {
    clearInterval(timer.current);
    if (!reduced) timer.current = setInterval(() => setIdx((i) => (i + 1) % GALLERY.length), 4500);
  }, [reduced]);

  useEffect(() => {
    restart();
    return () => clearInterval(timer.current);
  }, [restart]);

  const go = (n) => {
    setIdx((n + GALLERY.length) % GALLERY.length);
    restart();
  };

  const { src, label } = GALLERY[idx];

  return (
    <div className="relative">
      <div aria-hidden className="absolute -inset-10 rounded-[44px] blur-3xl opacity-70" style={{ background: 'radial-gradient(55% 55% at 50% 40%, rgba(245,192,24,0.34), rgba(7,26,73,0) 70%)' }} />
      <div className="relative rounded-2xl overflow-hidden border border-white/12" style={{ background: '#0a1640', boxShadow: '0 60px 120px -40px rgba(0,0,0,0.85), 0 0 0 1px rgba(255,255,255,0.05) inset' }}>
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/10">
          <span className="w-2.5 h-2.5 rounded-full bg-red-400/80" />
          <span className="w-2.5 h-2.5 rounded-full bg-yellow-400/80" />
          <span className="w-2.5 h-2.5 rounded-full bg-green-400/80" />
          <span className="ml-2 text-xs text-slate-400 tnum">app.autolink — {label}</span>
          <div className="ml-auto flex items-center gap-1.5">
            {GALLERY.map((g, i) => (
              <button
                key={g.label}
                onClick={() => go(i)}
                aria-label={g.label}
                className="rounded-full transition-all duration-300"
                style={{ width: i === idx ? 18 : 7, height: 7, background: i === idx ? GOLD : 'rgba(255,255,255,0.25)' }}
              />
            ))}
          </div>
        </div>
        <div className="relative overflow-hidden">
          <AnimatePresence mode="wait" initial={false}>
            <motion.img
              key={idx}
              src={src}
              alt={label}
              initial={{ opacity: 0, scale: 1.02 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.99 }}
              transition={{ duration: 0.4, ease: EASE }}
              className="w-full block cursor-zoom-in"
              onClick={() => onImageClick(src, label)}
              draggable={false}
            />
          </AnimatePresence>
          <button onClick={() => go(idx - 1)} aria-label="Previous" className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center text-white transition-colors">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button onClick={() => go(idx + 1)} aria-label="Next" className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center text-white transition-colors">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}

function Hero({ onImageClick }) {
  const reduced = useReducedMotion();
  const isMobile = useIsMobile();
  const sectionRef = useRef(null);

  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ['start start', 'end start'] });
  const stageY = useTransform(scrollYProgress, [0, 1], [0, reduced ? 0 : -50]);
  const stageOpacity = useTransform(scrollYProgress, [0, 0.95], [1, 0.3]);

  return (
    <section
      id="top"
      ref={sectionRef}
      className="relative overflow-hidden min-h-screen flex flex-col justify-center pt-28 pb-20"
      style={{ background: `radial-gradient(125% 80% at 50% -10%, ${NAVY} 0%, ${NAVY_DEEP} 50%, ${NAVY_INK} 100%)` }}
    >
      {/* emblem watermark */}
      <div aria-hidden className="absolute right-0 top-[6%] pr-2 sm:pr-10 pointer-events-none">
        <motion.img
          src={AutolinkIcon}
          alt=""
          className="block w-[440px] sm:w-[640px] max-w-[90vw] select-none"
          style={{ opacity: 0.07, filter: 'drop-shadow(0 0 70px rgba(245,192,24,0.45))' }}
          initial={{ rotate: -12 }}
          animate={reduced ? { rotate: -12 } : { rotate: -12, scale: [1, 1.05, 1], opacity: [0.05, 0.09, 0.05] }}
          transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
        />
      </div>

      {/* road floor */}
      <div aria-hidden className="absolute inset-x-0 bottom-0 h-[52%] pointer-events-none overflow-hidden" style={{ perspective: '520px', perspectiveOrigin: '50% 0%' }}>
        <div
          className="hero-road absolute inset-0"
          style={{
            transform: 'rotateX(75deg)',
            transformOrigin: '50% 100%',
            backgroundImage:
              'linear-gradient(rgba(245,192,24,0.22) 2px, transparent 2px), linear-gradient(90deg, rgba(245,192,24,0.14) 2px, transparent 2px)',
            backgroundSize: '64px 64px',
            maskImage: 'linear-gradient(to top, black 6%, transparent 72%)',
            WebkitMaskImage: 'linear-gradient(to top, black 6%, transparent 72%)',
          }}
        />
      </div>
      {/* horizon glow */}
      <div aria-hidden className="absolute inset-x-0 pointer-events-none" style={{ bottom: '52%', height: '160px', background: 'radial-gradient(60% 100% at 50% 100%, rgba(245,192,24,0.16), transparent 70%)' }} />

      <div className="relative max-w-5xl mx-auto px-5 lg:px-8 text-center w-full">
        <motion.a
          href="#features"
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: EASE }}
          className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-medium mb-6"
          style={{ color: GOLD, background: 'rgba(245,192,24,0.1)', border: '1px solid rgba(245,192,24,0.28)' }}
        >
          <Zap className="w-3.5 h-3.5" />
          The all-in-one dealership platform
        </motion.a>

        <motion.h1
          initial={{ opacity: 0, y: 26 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
          className="font-display font-extrabold text-white mx-auto max-w-4xl"
          style={{ fontSize: 'clamp(2.6rem, 6.6vw, 5rem)', lineHeight: 0.98, letterSpacing: '-0.04em', textWrap: 'balance' }}
        >
          Run your dealership <span style={{ color: GOLD }}>like a pro</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.22, ease: EASE }}
          className="mt-6 mx-auto max-w-xl text-lg text-slate-300 leading-relaxed"
        >
          Inventory, leads, customers, sales and documents — every part of your
          lot, finally in one place built for car dealers.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.34, ease: EASE }}
          className="mt-9 flex flex-col sm:flex-row gap-3 justify-center"
        >
          <Magnetic strength={0.35}>
            <a href={`${APP_URL}/register`} className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl font-semibold text-base text-[#071a49] transition-transform active:scale-[0.97]" style={{ background: GOLD, boxShadow: '0 18px 40px -14px rgba(245,192,24,0.6)' }}>
              Start your free trial
              <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
            </a>
          </Magnetic>
          <a href="#features" className="inline-flex items-center justify-center px-7 py-3.5 rounded-xl font-semibold text-base text-white border border-white/15 hover:bg-white/5 transition-colors">
            See all features
          </a>
        </motion.div>

        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.5 }} className="mt-4 text-sm text-slate-400">
          30-days free trial · No credit card required · Cancel anytime
        </motion.p>

        {/* carousel "billboard" on the road */}
        <motion.div style={{ y: stageY, opacity: stageOpacity }} className="mt-14 mx-auto max-w-4xl">
          <motion.div
            initial={reduced ? false : { opacity: 0, y: 50, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1, delay: 0.4, ease: EASE }}
            className="relative"
          >
            <HeroCarousel onImageClick={onImageClick} />

            {!isMobile && (
              <>
                <FloatChip className="-left-7 top-1/4" delay={0.9} float={11}>
                  <div className="flex items-center gap-2.5">
                    <span className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(245,192,24,0.16)' }}>
                      <Bell className="w-4 h-4" style={{ color: GOLD }} />
                    </span>
                    <div className="text-left">
                      <div className="text-white text-sm font-semibold leading-tight">New lead</div>
                      <div className="text-slate-400 text-xs leading-tight">Lisa T. · $45,000</div>
                    </div>
                  </div>
                </FloatChip>

                <FloatChip className="-right-6 top-1/3" delay={1.15} float={13}>
                  <div className="text-left">
                    <div className="text-slate-400 text-[11px] uppercase tracking-wide">Total revenue</div>
                    <div className="text-white font-display font-bold text-lg tnum">$238,769</div>
                    <div className="text-xs font-medium" style={{ color: '#4ade80' }}>↑ 12% this month</div>
                  </div>
                </FloatChip>
              </>
            )}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ *
 *  Product tour — manual, arrow-controlled annotated screenshots
 * ------------------------------------------------------------------ */
function StoryText({ s, compact = false }) {
  const Icon = s.icon;
  return (
    <div>
      <span className="inline-flex items-center gap-2 text-sm font-semibold px-3 py-1 rounded-full mb-5" style={{ color: GOLD_DEEP, background: 'rgba(245,192,24,0.16)', border: '1px solid rgba(245,192,24,0.4)' }}>
        <Icon className="w-3.5 h-3.5" />
        {s.tag}
      </span>
      <h3 className="font-display font-bold" style={{ color: INK, fontSize: compact ? 'clamp(1.7rem, 3vw, 2.4rem)' : 'clamp(2rem, 3.6vw, 3.1rem)', lineHeight: 1.06, letterSpacing: '-0.025em', textWrap: 'balance' }}>
        {s.title}
      </h3>
      <p className="mt-5 text-lg leading-relaxed max-w-md" style={{ color: BODY }}>{s.body}</p>
      <ul className="mt-7 space-y-3">
        {s.points.map((p) => (
          <li key={p} className="flex items-center gap-3" style={{ color: '#334155' }}>
            <span className="w-5 h-5 rounded-full flex items-center justify-center shrink-0" style={{ background: 'rgba(245,192,24,0.2)' }}>
              <Check className="w-3 h-3" style={{ color: GOLD_DEEP }} />
            </span>
            {p}
          </li>
        ))}
      </ul>
    </div>
  );
}

function TourArrow({ dir, onClick, overlay = false }) {
  const Icon = dir === 'prev' ? ChevronLeft : ChevronRight;
  if (overlay) {
    return (
      <button
        onClick={onClick}
        aria-label={dir === 'prev' ? 'Previous screen' : 'Next screen'}
        className={`absolute ${dir === 'prev' ? 'left-2' : 'right-2'} top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full flex items-center justify-center text-white transition-transform active:scale-95`}
        style={{ background: 'rgba(7,26,73,0.8)' }}
      >
        <Icon className="w-5 h-5" />
      </button>
    );
  }
  return (
    <button
      onClick={onClick}
      aria-label={dir === 'prev' ? 'Previous' : 'Next'}
      className="w-11 h-11 rounded-full flex items-center justify-center transition-transform active:scale-95 hover:opacity-90"
      style={{ background: NAVY, color: '#fff' }}
    >
      <Icon className="w-5 h-5" />
    </button>
  );
}

function FeatureStory({ onImageClick }) {
  const [active, setActive] = useState(0);
  const go = useCallback((n) => setActive((n + STORY.length) % STORY.length), []);
  const s = STORY[active];

  return (
    <section id="features" className="py-24" style={{ background: SURFACE }}>
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <Reveal className="max-w-2xl">
          <p className="text-sm font-semibold tracking-wide" style={{ color: GOLD_DEEP }}>THE PRODUCT TOUR</p>
          <h2 className="font-display font-bold mt-3" style={{ color: INK, fontSize: 'clamp(2rem, 5vw, 3.5rem)', letterSpacing: '-0.03em', textWrap: 'balance' }}>
            One platform for every corner of the lot
          </h2>
          <p className="mt-4 text-lg" style={{ color: MUTED }}>Use the arrows to explore each part of AutoLink, one screen at a time.</p>
        </Reveal>

        <div className="mt-12 grid items-center gap-8 lg:gap-14 md:grid-cols-[0.8fr_1.2fr]">
          {/* left: text + controls */}
          <div className="relative order-2 md:order-1">
            <div className="flex gap-2.5 mb-7">
              {STORY.map((st, i) => (
                <button
                  key={st.tag}
                  onClick={() => setActive(i)}
                  aria-label={st.tag}
                  className="h-1.5 rounded-full transition-all duration-300"
                  style={{ width: i === active ? 38 : 16, background: i === active ? GOLD : 'rgba(7,26,73,0.15)' }}
                />
              ))}
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.4, ease: EASE }}
              >
                <StoryText s={s} compact />
              </motion.div>
            </AnimatePresence>
          </div>

          {/* right: screenshot + overlay arrows */}
          <div className="relative order-1 md:order-2">
            <span aria-hidden className="absolute -top-12 -right-2 font-display font-extrabold pointer-events-none select-none" style={{ fontSize: 'clamp(4rem, 9vw, 9rem)', color: 'rgba(7,26,73,0.07)', lineHeight: 1 }}>
              {s.kicker}
            </span>
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                initial={{ opacity: 0, scale: 0.97, filter: 'blur(6px)' }}
                animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
                exit={{ opacity: 0, scale: 0.98, filter: 'blur(6px)' }}
                transition={{ duration: 0.45, ease: EASE }}
              >
                <AppFrame src={s.img} label={s.tag} notes={s.notes} onClick={() => onImageClick(s.img, s.title)} />
              </motion.div>
            </AnimatePresence>
            <TourArrow dir="prev" overlay onClick={() => go(active - 1)} />
            <TourArrow dir="next" overlay onClick={() => go(active + 1)} />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ *
 *  More capabilities — clean icon list, no screenshots
 * ------------------------------------------------------------------ */
function MoreFeatures() {
  return (
    <section className="py-24" style={{ background: SURFACE_ALT }}>
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <Reveal className="max-w-2xl mb-12">
          <h2 className="font-display font-bold" style={{ color: INK, fontSize: 'clamp(2rem, 5vw, 3.25rem)', letterSpacing: '-0.03em', textWrap: 'balance' }}>
            And everything around the sale
          </h2>
          <p className="mt-4 text-lg" style={{ color: MUTED }}>The day-to-day work that keeps a dealership moving — handled in the same place.</p>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-y-2">
          {MORE.map((m, i) => {
            const Icon = m.icon;
            return (
              <Reveal key={m.title} delay={(i % 3) * 0.06}>
                <div className="group flex gap-4 py-6 px-1 border-b h-full" style={{ borderColor: LINE }}>
                  <span className="shrink-0 w-11 h-11 rounded-xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110" style={{ background: 'rgba(245,192,24,0.16)' }}>
                    <Icon className="w-5 h-5" style={{ color: GOLD_DEEP }} />
                  </span>
                  <div>
                    <h3 className="font-display font-semibold text-lg" style={{ color: INK }}>{m.title}</h3>
                    <p className="mt-1 text-[15px] leading-relaxed" style={{ color: MUTED }}>{m.body}</p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ *
 *  Pricing
 * ------------------------------------------------------------------ */
function Pricing() {
  const [annual, setAnnual] = useState(false);
  const getPrice = (m) => (annual ? Math.round(m * 0.8) : m);

  return (
    <section id="pricing" className="py-24" style={{ background: SURFACE }}>
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <Reveal className="text-center mb-12">
          <h2 className="font-display font-bold" style={{ color: INK, fontSize: 'clamp(2rem, 5vw, 3.25rem)', letterSpacing: '-0.03em', textWrap: 'balance' }}>
            Simple, transparent pricing
          </h2>
          <p className="mt-4 text-lg" style={{ color: MUTED }}>No hidden fees. No per-vehicle charges. One flat monthly rate.</p>
          <div className="inline-flex items-center gap-1 mt-8 p-1 rounded-xl border" style={{ background: '#fff', borderColor: LINE }}>
            <button onClick={() => setAnnual(false)} className="px-5 py-2 rounded-lg text-sm font-medium transition-colors" style={{ background: !annual ? GOLD : 'transparent', color: !annual ? NAVY : MUTED }}>Monthly</button>
            <button onClick={() => setAnnual(true)} className="px-5 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2" style={{ background: annual ? GOLD : 'transparent', color: annual ? NAVY : MUTED }}>
              Annual
              <span className="text-[11px] px-1.5 py-0.5 rounded-full font-semibold" style={{ background: annual ? 'rgba(7,26,73,0.15)' : 'rgba(34,197,94,0.14)', color: annual ? NAVY : '#15803d' }}>−20%</span>
            </button>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto items-stretch">
          {PLANS.map((p, i) => {
            const dark = p.highlight;
            return (
              <Reveal key={p.name} delay={i * 0.1}>
                <div
                  className="relative h-full rounded-2xl p-8 flex flex-col border"
                  style={{
                    background: dark ? NAVY : SURFACE,
                    borderColor: dark ? 'rgba(245,192,24,0.5)' : LINE,
                    boxShadow: dark ? '0 30px 80px -34px rgba(7,26,73,0.55)' : '0 20px 50px -30px rgba(7,26,73,0.18)',
                  }}
                >
                  {p.badge && <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-xs font-bold px-3 py-1 rounded-full" style={{ background: GOLD, color: NAVY }}>{p.badge}</span>}
                  <h3 className="font-display font-semibold text-xl" style={{ color: dark ? '#fff' : INK }}>{p.name}</h3>
                  <p className="mt-1 text-sm" style={{ color: dark ? '#94a3b8' : MUTED }}>{p.description}</p>
                  <div className="mt-5 flex items-baseline gap-1.5">
                    <span className="font-display font-bold tnum" style={{ color: dark ? '#fff' : INK, fontSize: '2.75rem', letterSpacing: '-0.03em' }}>${getPrice(p.price)}</span>
                    <span className="text-sm" style={{ color: dark ? '#94a3b8' : MUTED }}>/month</span>
                  </div>
                  {annual && !p.comingSoon && <div className="text-xs mt-1" style={{ color: '#16a34a' }}>Billed annually</div>}
                  <ul className="mt-7 space-y-3 flex-1">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-sm" style={{ color: dark ? '#cbd5e1' : '#334155' }}>
                        <span className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5" style={{ background: dark ? 'rgba(245,192,24,0.2)' : 'rgba(245,192,24,0.18)' }}>
                          <Check className="w-3 h-3" style={{ color: dark ? GOLD : GOLD_DEEP }} />
                        </span>
                        {f}
                      </li>
                    ))}
                  </ul>
                  <button
                    disabled={p.comingSoon}
                    onClick={() => !p.comingSoon && (window.location.href = `${APP_URL}/register`)}
                    className="mt-8 w-full py-3 rounded-xl font-semibold text-sm transition-transform active:scale-[0.98] disabled:cursor-not-allowed"
                    style={
                      p.comingSoon
                        ? { background: 'rgba(255,255,255,0.1)', color: '#94a3b8' }
                        : { background: GOLD, color: NAVY }
                    }
                  >
                    {p.cta}
                  </button>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ *
 *  FAQ
 * ------------------------------------------------------------------ */
function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border overflow-hidden" style={{ background: SURFACE, borderColor: LINE }}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-4 px-6 py-4 text-left font-medium hover:bg-slate-50 transition-colors" style={{ color: INK }} aria-expanded={open}>
        <span>{q}</span>
        <ChevronDown className="w-5 h-5 shrink-0 transition-transform duration-300" style={{ color: MUTED, transform: open ? 'rotate(180deg)' : 'none' }} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3, ease: EASE }} style={{ overflow: 'hidden' }}>
            <p className="px-6 pb-5 pt-1 leading-relaxed" style={{ color: MUTED }}>{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function FAQ() {
  return (
    <section id="faq" className="py-24" style={{ background: SURFACE }}>
      <div className="max-w-3xl mx-auto px-5 lg:px-8">
        <Reveal className="text-center mb-12">
          <h2 className="font-display font-bold" style={{ color: INK, fontSize: 'clamp(2rem, 5vw, 3.25rem)', letterSpacing: '-0.03em', textWrap: 'balance' }}>
            Frequently asked questions
          </h2>
        </Reveal>
        <div className="space-y-3">
          {FAQS.map((f) => <FaqItem key={f.q} {...f} />)}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ *
 *  Final CTA — AutoLink emblem background (contained, right side)
 * ------------------------------------------------------------------ */
function FinalCTA() {
  const reduced = useReducedMotion();
  return (
    <section className="relative overflow-hidden py-32" style={{ background: NAVY }}>
      <div aria-hidden className="absolute inset-0 pointer-events-none">
        <div className="absolute right-0 top-1/2 -translate-y-1/2 pr-2 sm:pr-8">
          <div className="relative">
            <div className="absolute -inset-16 rounded-full" style={{ background: 'radial-gradient(circle, rgba(245,192,24,0.26) 0%, rgba(245,192,24,0) 62%)' }} />
            <motion.img
              src={AutolinkIcon}
              alt=""
              className="relative block w-[320px] sm:w-[500px] max-w-[52vw] select-none"
              style={{ opacity: 0.16, filter: 'drop-shadow(0 0 70px rgba(245,192,24,0.5))' }}
              initial={{ rotate: -12 }}
              animate={reduced ? { rotate: -12 } : { rotate: -12, scale: [1, 1.04, 1] }}
              transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
            />
          </div>
        </div>
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
            maskImage: 'radial-gradient(60% 60% at 50% 50%, black, transparent 75%)',
            WebkitMaskImage: 'radial-gradient(60% 60% at 50% 50%, black, transparent 75%)',
          }}
        />
      </div>

      <div className="relative max-w-3xl mx-auto px-5 lg:px-8 text-center">
        <Reveal>
          <h2 className="font-display font-bold text-white" style={{ fontSize: 'clamp(2.2rem, 6vw, 4rem)', letterSpacing: '-0.035em', lineHeight: 1.04, textWrap: 'balance' }}>
            Ready to run your dealership like a pro?
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-5 text-slate-300 text-lg">Join the dealers who ditched the spreadsheets. Start your free 14-day trial — set up in under five minutes.</p>
        </Reveal>
        <Reveal delay={0.2}>
          <div className="mt-9">
            <Magnetic strength={0.35}>
              <a href={`${APP_URL}/register`} className="group inline-flex items-center gap-2 px-9 py-4 rounded-xl font-semibold text-base text-[#071a49] transition-transform active:scale-[0.97]" style={{ background: GOLD, boxShadow: '0 20px 50px -18px rgba(245,192,24,0.7)' }}>
                Get started free
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </a>
            </Magnetic>
          </div>
        </Reveal>
        <Reveal delay={0.28}>
          <p className="mt-4 text-sm text-slate-400">30-days free trial · No credit card required</p>
        </Reveal>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ *
 *  Footer
 * ------------------------------------------------------------------ */
function Footer() {
  return (
    <footer className="py-12 border-t border-white/10" style={{ background: NAVY_INK }}>
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
          <img src={AutolinkLogo} alt="AutoLink" className="h-7 w-auto" style={{ filter: 'brightness(0) invert(1)' }} />
          <div className="flex gap-7 text-sm">
            {NAV_LINKS.map((l) => <a key={l.label} href={l.href} className="text-slate-400 hover:text-white transition-colors">{l.label}</a>)}
          </div>
          <div className="flex flex-col items-center md:items-end gap-2 text-sm">
            <div className="flex gap-5">
              <Link to="/privacy" className="text-slate-400 hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="text-slate-400 hover:text-white transition-colors">Terms &amp; Conditions</Link>
            </div>
            <div className="text-slate-500">© 2026 AutoLink. All rights reserved.</div>
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ------------------------------------------------------------------ *
 *  Page
 * ------------------------------------------------------------------ */
export default function Landing() {
  const reduced = useReducedMotion();
  useLenisScroll(!reduced);

  const [lightbox, setLightbox] = useState(null);
  const openLightbox = useCallback((src, alt) => setLightbox({ src, alt }), []);
  const closeLightbox = useCallback(() => setLightbox(null), []);

  return (
    <div style={{ background: SURFACE }}>
      <ScrollProgress />
      <CursorGlow />
      <Navbar />
      <main>
        <Hero onImageClick={openLightbox} />
        <FeatureStory onImageClick={openLightbox} />
        <MoreFeatures />
        <Pricing />
        <FAQ />
        <FinalCTA />
      </main>
      <Footer />

      <AnimatePresence>
        {lightbox && <Lightbox src={lightbox.src} alt={lightbox.alt} onClose={closeLightbox} />}
      </AnimatePresence>
    </div>
  );
}
